import { useSearchParams } from "react-router-dom";
import { Row, Col, Select, Tabs, Typography, Space } from "antd";
import { ShoppingBag, CalendarCheck } from "lucide-react";
import { useCommunitySelector } from "hooks/useCommunitySelector/useCommunitySelector";
import AnalyticsSummary from "./_blocks/AnalyticsSummary";
import OrdersTab from "./_blocks/OrdersTab";
import BookingsTab from "./_blocks/BookingsTab";
import useAuthStore from "stores/AuthStore/AuthStore";

const { Title, Text } = Typography;

const TAB_ITEMS = [
  {
    key: "orders",
    label: (
      <Space size={6}>
        <ShoppingBag className="h-4 w-4" />
        <Text>Orders</Text>
      </Space>
    ),
    children: <OrdersTab />,
  },
  {
    key: "bookings",
    label: (
      <Space size={6}>
        <CalendarCheck className="h-4 w-4" />
        <Text>Bookings</Text>
      </Space>
    ),
    children: <BookingsTab />,
  },
];

function Orders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isRootUser } = useAuthStore();
  const {
    communityOptions,
    selectedCommunityId,
    setSelectedCommunityId,
    loading,
  } = useCommunitySelector({
    initialCommunityId: searchParams.get("community_id"),
    isRootUser,
  });

  const activeTab = searchParams.get("tab") ?? "orders";

  const mergeParams = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === null || v === "") next.delete(k);
        else next.set(k, v);
      });
      return next;
    });
  };

  const handleCommunityChange = (val) => {
    setSelectedCommunityId(val ?? null);
    mergeParams({ community_id: val ?? "" });
  };

  const handleTabChange = (key) => {
    mergeParams({ tab: key === "orders" ? "" : key });
  };

  return (
    <div className="space-y-6">
      <Row justify="space-between" align="top" gutter={[16, 16]}>
        <Col xs={24} sm={14}>
          <Title level={3} style={{ margin: 0 }}>
            Orders & Bookings
          </Title>
          <Text type="secondary">
            Track and manage all transactions for your account
          </Text>
        </Col>
        {isRootUser && (
          <Col
            xs={24}
            sm={10}
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Row gutter={8} align="middle" wrap={false}>
              <Col>
                <Text type="secondary">Community:</Text>
              </Col>
              <Col>
                <Select
                  style={{ minWidth: 220 }}
                  placeholder="All Communities"
                  options={[
                    { value: null, label: "All Communities" },
                    ...communityOptions,
                  ]}
                  value={selectedCommunityId}
                  onChange={handleCommunityChange}
                  loading={loading}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>

      <AnalyticsSummary communityId={selectedCommunityId} />

      <div className="rounded-2xl border border-border/50 bg-card overflow-hidden shadow-card">
        <div className="px-4 md:px-6">
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={TAB_ITEMS}
          />
        </div>
      </div>
    </div>
  );
}

export default Orders;
